import { formatFlightRange } from "../utils/flightDates";
import { REPORTING_TIME_ZONE, mergeSourceCalendarPlatform, sourceCalendarsFor } from "../utils/reportingCalendar";
import { sanitizeGovernedWindowLabel } from "./reportingWindowLabel";

function bounds(value) {
  const start = value?.start ?? value?.startDate;
  const end = value?.end ?? value?.endDate;
  const validDay = day => typeof day === "string" && /^\d{4}-\d{2}-\d{2}$/.test(day) &&
    Number.isFinite(Date.parse(`${day}T12:00:00Z`)) &&
    new Date(`${day}T12:00:00Z`).toISOString().slice(0, 10) === day;
  if (!validDay(start) || !validDay(end) || start > end) return null;
  const timeZone = value.timeZone ?? REPORTING_TIME_ZONE;
  if (typeof timeZone !== "string" || !timeZone.trim()) return null;
  try { new Intl.DateTimeFormat("en-US", { timeZone }); } catch { return null; }
  return { start, end, timeZone };
}

// A selector is request context. Only the reader's effective period can name
// the values it returned; never fill a missing effective period from a flight.
export function readToplineReportingWindow(topline) {
  const raw = topline?.raw || {};
  const effective = Object.prototype.hasOwnProperty.call(raw, "effectiveReportingWindow")
    ? bounds(raw.effectiveReportingWindow) : bounds({
    start: raw.startDate, end: raw.endDate, timeZone: raw.reportingTimeframe?.timeZone,
  }) || bounds(topline?.analysisWindow);
  const requested = Object.prototype.hasOwnProperty.call(raw, "requestedReportingWindow")
    ? bounds(raw.requestedReportingWindow) : bounds({
    start: raw.requestedStartDate, end: raw.requestedEndDate,
  }) || bounds(topline?.requestedAnalysisWindow);
  const described = bounds(topline?.analysisWindow);
  const describedRequested = bounds(topline?.requestedAnalysisWindow);
  const matches = (a, b) => a && b && a.start === b.start && a.end === b.end;
  // The calendar the answer itself stated (null when it stated none — an
  // answer without canonical evidence). A topline built before answers carried
  // the statement keeps its window's own zone.
  const statedTimeZone = Object.prototype.hasOwnProperty.call(raw, "reportingTimeZone")
    ? raw.reportingTimeZone || null
    : effective?.timeZone ?? null;
  return {
    effective,
    requested,
    statedTimeZone,
    key: effective ? `${effective.start}/${effective.end}/${effective.timeZone}` : null,
    label: effective
      ? (matches(effective, described) && (sanitizeGovernedWindowLabel(topline?.windowLabel) || sanitizeGovernedWindowLabel(topline?.analysisWindow?.label))) ||
        formatFlightRange(effective.start, effective.end)
      : null,
    requestedLabel: requested
      ? (matches(requested, describedRequested) && (sanitizeGovernedWindowLabel(topline?.requestedWindowLabel) || sanitizeGovernedWindowLabel(topline?.requestedAnalysisWindow?.label))) ||
        formatFlightRange(requested.start, requested.end)
      : null,
    recovered: raw.recoveredFromAutoFlightWindow === true,
  };
}

// Sum and rank only one actual period. Prefer the primary period when a
// channel recovered elsewhere; several incompatible primary periods cannot
// establish a common aggregate. Missing dates are not evidence of equality.
export function selectAggregateReportingWindow(entries) {
  const read = entries.map(entry => ({ ...entry, window: readToplineReportingWindow(entry.topline) }));
  /*
   * Periods are compared on their dates and the calendar their answers STATE.
   * An answer that states no calendar is calendar-unknown: for the same dates
   * it joins the one calendar its peers state, and never forms a key of its
   * own from the portal's default zone (which would split a Pacific Meta
   * answer from an unstated Metricool answer for the very same days).
   */
  const datesOf = window => window.effective ? `${window.effective.start}/${window.effective.end}` : null;
  const statedByDates = new Map();
  read.forEach(entry => {
    const dates = datesOf(entry.window);
    if (!dates || !entry.window.statedTimeZone) return;
    if (!statedByDates.has(dates)) statedByDates.set(dates, new Set());
    statedByDates.get(dates).add(entry.window.statedTimeZone);
  });
  const periodKey = window => {
    const dates = datesOf(window);
    if (!dates) return null;
    if (window.statedTimeZone) return `${dates}/${window.statedTimeZone}`;
    const zones = statedByDates.get(dates);
    return zones && zones.size === 1 ? `${dates}/${[...zones][0]}` : `${dates}/unstated`;
  };
  const windows = read.map(entry => ({ ...entry, window: { ...entry.window, key: periodKey(entry.window) } }));
  const known = windows.filter(entry => entry.window.key);
  const primaryKeys = new Set(known.filter(entry => !entry.window.recovered).map(entry => entry.window.key));
  const allKeys = new Set(known.map(entry => entry.window.key));
  const key = primaryKeys.size === 1 ? [...primaryKeys][0]
    : primaryKeys.size === 0 && allKeys.size === 1 ? [...allKeys][0] : null;
  const included = key ? known.filter(entry => entry.window.key === key) : [];
  const excluded = windows.filter(entry => !key || entry.window.key !== key);
  // Name the period from an answer that stated its calendar, when one did.
  const chosen = (included.find(entry => entry.window.statedTimeZone) || included[0])?.window || null;
  /*
   * Calendar provenance travels with the total. Joining its stated peers keeps
   * an unstated answer's figures in the sum (compatibility), but never makes
   * its calendar known: it is listed as not stated, and the period's zone is
   * only ever one an answer stated — never the portal's default. The
   * requested calendars are the request's, kept apart from the response's.
   */
  const statedZones = [...new Set(included.map(entry => entry.window.statedTimeZone).filter(Boolean))];
  const responseTimeZone = statedZones.length === 1 ? statedZones[0] : null;
  const platformRef = entry => ({ key: entry.key, label: entry.label });
  const unstatedPlatforms = included.filter(entry => !entry.window.statedTimeZone).map(platformRef);
  /*
   * The provider SOURCE calendars behind the total, merged from each answer's
   * own statement (`raw.reportingCalendar.normalization`). The total is
   * Eastern-normalized only when every included answer says its platforms'
   * days are Eastern days; one that says nothing about its sources leaves the
   * total "not stated", never normalized.
   */
  // Each answer describes every platform its advertiser has: keep only the
  // platform(s) this entry contributes to the total.
  const normalizations = included.map(entry =>
    sourceCalendarsFor(entry.topline?.raw?.reportingCalendar?.normalization, entry.key));
  const stated = normalizations.filter(Boolean);
  const platforms = {};
  stated.forEach(n => Object.entries(n.platforms).forEach(([key, value]) => {
    platforms[key] = mergeSourceCalendarPlatform(platforms[key], value);
  }));
  const keysWith = basis => Object.keys(platforms).filter(key => platforms[key].dayBasis === basis).sort();
  const sourceCalendars = {
    status: !included.length ? "unavailable"
      : !stated.length ? "not_stated"
        : stated.length === included.length && Object.values(platforms).every(p => p.dayBasis === "eastern_native")
          ? "eastern_normalized" : "mixed_source_calendars",
    platforms,
    easternNativePlatforms: keysWith("eastern_native"),
    sourceCalendarPlatforms: keysWith("source_calendar_days"),
    unverifiedPlatforms: keysWith("source_calendar_unverified"),
    // Did any answer state source calendars at all (a backend that states
    // them), as opposed to none (a Pacific-era backend, which says nothing)?
    statementsPresent: included.some(entry => typeof entry.topline?.raw?.reportingCalendar?.normalization?.status === "string"),
    // An answer that stated no calendar at all is already named as such.
    sourcesNotStatedPlatforms: included
      .filter((entry, index) => !normalizations[index] && entry.window.statedTimeZone)
      .map(platformRef),
  };
  const reportingCalendar = {
    requestedTimeZones: [...new Set(windows.map(entry => entry.window.requested?.timeZone).filter(Boolean))],
    responseTimeZone,
    status: !included.length ? "unavailable"
      : !unstatedPlatforms.length ? "stated"
        : responseTimeZone ? "partly_unstated" : "unstated",
    statedPlatforms: included.filter(entry => entry.window.statedTimeZone)
      .map(entry => ({ ...platformRef(entry), timeZone: entry.window.statedTimeZone })),
    unstatedPlatforms,
    sourceCalendars,
  };
  return {
    included,
    metadata: {
      reportingWindowStatus: included.length ? (excluded.length ? "partial" : "complete") : "unavailable",
      effectiveReportingWindow: chosen?.effective ? { ...chosen.effective, timeZone: responseTimeZone } : null,
      reportingCalendar,
      windowLabel: chosen?.label || null,
      requestedReportingWindows: [...new Map(windows.filter(entry => entry.window.requested).map(entry => [
        JSON.stringify(entry.window.requested), { ...entry.window.requested, label: entry.window.requestedLabel },
      ])).values()],
      excludedWindowPlatforms: excluded.map(entry => ({
        key: entry.key, label: entry.label,
        effectiveReportingWindow: entry.window.effective
          ? { ...entry.window.effective, timeZone: entry.window.statedTimeZone } : null,
        windowLabel: entry.window.label,
        reason: entry.window.key ? "different_reporting_window" : "reporting_window_unavailable",
      })),
    },
  };
}
